import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, Clock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const statusStyle = {
  requested: "bg-amber-100 text-amber-800",
  confirmed: "bg-emerald-100 text-emerald-800",
  completed: "bg-sky-100 text-sky-800",
  cancelled: "bg-slate-200 text-slate-600",
};

export default function MyBookings() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () =>
    api
      .get("/appointments/mine")
      .then(({ data }) => setItems(data))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const cancel = async (id) => {
    await api.delete(`/appointments/${id}`);
    toast.success("Appointment cancelled");
    load();
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-20" data-testid="my-bookings-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl sm:text-4xl tracking-tight text-slate-900">My Bookings</h1>
          <p className="mt-3 text-sm text-slate-600">
            Signed in as <span className="text-slate-800">{user?.email}</span>
          </p>
        </div>
        <Link to="/book">
          <Button
            className="rounded-full h-11 px-7 bg-blue-900 text-white hover:bg-blue-950 hover:text-white"
            data-testid="my-bookings-new-btn"
          >
            Book Appointment
          </Button>
        </Link>
      </div>

      <div className="mt-10 space-y-4">
        {loading ? (
          <div className="h-28 rounded-2xl bg-slate-200/60 animate-pulse" />
        ) : items.length === 0 ? (
          <div
            className="border border-dashed border-slate-300 rounded-2xl py-20 text-center"
            data-testid="my-bookings-empty"
          >
            <p className="font-display text-2xl text-slate-800">No appointments yet</p>
            <p className="mt-3 text-sm text-slate-500">Your booked visits will appear here.</p>
          </div>
        ) : (
          items.map((a) => (
            <div
              key={a.id}
              data-testid={`booking-row-${a.id}`}
              className="bg-white border border-slate-100 rounded-2xl p-6 sm:p-8 flex flex-wrap gap-6 items-start justify-between"
            >
              <div>
                <div className="flex items-center gap-3">
                  <h3 className="text-lg font-medium text-slate-900">{a.service}</h3>
                  <Badge className={`rounded-full border-0 ${statusStyle[a.status]}`}>{a.status}</Badge>
                </div>
                <p className="mt-3 text-sm text-slate-600 flex flex-wrap gap-5">
                  <span className="flex items-center gap-2">
                    <CalendarDays className="w-4 h-4 text-sky-500" />
                    {a.date}
                  </span>
                  <span className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-sky-500" />
                    {a.time_slot}
                  </span>
                </p>
                {a.notes && <p className="mt-3 text-sm text-slate-500 max-w-lg">{a.notes}</p>}
              </div>
              {a.status !== "cancelled" && a.status !== "completed" && (
                <Button
                  variant="outline"
                  className="rounded-full"
                  onClick={() => cancel(a.id)}
                  data-testid={`booking-cancel-${a.id}`}
                >
                  Cancel
                </Button>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
